import {
  DeviceCapabilities,
  Orientation,
  PointerType,
  ScreenSize
} from './useDeviceCapabilities';

/**
 * Breakpoint values in pixels (matching MUI defaults)
 */
export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536
} as const;

/**
 * Tablet detection: touch device with screen width between 600px and 1200px
 */
export const TABLET_MIN_WIDTH = BREAKPOINTS.sm;
export const TABLET_MAX_WIDTH = BREAKPOINTS.lg;

/**
 * Get the screen size category based on width
 */
export function getScreenSize(width: number): ScreenSize {
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
}

/**
 * Get orientation based on screen dimensions
 */
export function getOrientation(width: number, height: number): Orientation {
  return height > width ? 'portrait' : 'landscape';
}

/**
 * Determine if device is a tablet based on touch capability and screen width
 */
export function getIsTablet(
  isTouchDevice: boolean,
  screenWidth: number
): boolean {
  return (
    isTouchDevice &&
    screenWidth >= TABLET_MIN_WIDTH &&
    screenWidth < TABLET_MAX_WIDTH
  );
}

/**
 * Determine if device is mobile based on touch capability and screen width
 */
export function getIsMobile(
  isTouchDevice: boolean,
  screenWidth: number
): boolean {
  return isTouchDevice && screenWidth < TABLET_MIN_WIDTH;
}

/**
 * Determine if device is desktop
 * Desktop: not a touch device OR large screen (even if touch-enabled)
 */
export function getIsDesktop(
  isTouchDevice: boolean,
  screenWidth: number
): boolean {
  const isMobile = getIsMobile(isTouchDevice, screenWidth);
  return !isTouchDevice || (screenWidth >= TABLET_MAX_WIDTH && !isMobile);
}

/**
 * Get pointer type from matchMedia result
 */
export function getPointerTypeFromMatches(
  coarseMatches: boolean,
  fineMatches: boolean
): PointerType {
  if (coarseMatches) return 'coarse';
  if (fineMatches) return 'fine';
  return 'none';
}

/**
 * Determine if device is touch-enabled
 */
export function getIsTouchDevice(
  hasCoarsePointer: boolean,
  maxTouchPoints: number
): boolean {
  return hasCoarsePointer || maxTouchPoints > 0;
}

/**
 * SSR fallback capabilities - assume desktop
 */
export function getSSRFallbackCapabilities(): DeviceCapabilities {
  return {
    isTouchDevice: false,
    isTablet: false,
    isMobile: false,
    isDesktop: true,
    screenSize: 'lg',
    pointerType: 'fine',
    hasHover: true,
    orientation: 'landscape',
    screenWidth: 1920,
    screenHeight: 1080
  };
}

/**
 * Calculate all device capabilities from raw values
 */
export function calculateDeviceCapabilities(params: {
  screenWidth: number;
  screenHeight: number;
  hasCoarsePointer: boolean;
  hasFinePointer: boolean;
  hasHover: boolean;
  maxTouchPoints: number;
}): DeviceCapabilities {
  const {
    screenWidth,
    screenHeight,
    hasCoarsePointer,
    hasFinePointer,
    hasHover,
    maxTouchPoints
  } = params;

  const screenSize = getScreenSize(screenWidth);
  const pointerType = getPointerTypeFromMatches(
    hasCoarsePointer,
    hasFinePointer
  );
  const orientation = getOrientation(screenWidth, screenHeight);
  const isTouchDevice = getIsTouchDevice(hasCoarsePointer, maxTouchPoints);
  const isTablet = getIsTablet(isTouchDevice, screenWidth);
  const isMobile = getIsMobile(isTouchDevice, screenWidth);
  const isDesktop = getIsDesktop(isTouchDevice, screenWidth);

  return {
    isTouchDevice,
    isTablet,
    isMobile,
    isDesktop,
    screenSize,
    pointerType,
    hasHover,
    orientation,
    screenWidth,
    screenHeight
  };
}
